import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Hall } from '@weblite/ts-action/hall'
import { HallAdvancedSettingsOptions } from '$comp/_app/components/leftPanel/components/define-hall-advanced-settings'
import {
  PremiumFeatureType,
  showBuyPremiumFeatureDialog,
} from '$comp/modals/buy-premium-feature-dialog'
import {
  COURSE_VITRINE_TAG,
  DISABLE_WATERMARK_TAG,
  HIDE_MEMBER_COUNT_TAG,
  ISSUE_TAG,
  SUPER_ROOM_TAG,
  VOD_ACTIVATED_TAG,
} from '$logic/hall/constant'
import { addTagToHallTags, removeTagFromHallTags } from '$logic/hall/helper'
import { useIsPremiumPlanEnabled } from '$logic/premium-features/react-hooks'
import { useUserSessionsLimit } from '$logic/hall/react-hook'
import { openUserSessionsLimit } from '$logic/router'

export interface HallAdvancedSettingsRefApi {
  getCourseVitrine: () => boolean
  getIssue: () => boolean
  getProtectContent: () => boolean
  getTags: () => string[]
  getVodActivated: () => boolean
  getHideMemberCount: () => boolean
  getWatermarkDisabled: () => boolean
  getSuperRoom: () => boolean
}

export const EditHallAdvancedSettings = forwardRef(
  function HallAdvancedSettings(
    { hall }: { hall: Hall },
    ref: ForwardedRef<HallAdvancedSettingsRefApi>,
  ) {
    const [tags, setTags] = useState<string[]>(hall.tags || [])

    useEffect(() => {
      setTags(hall.tags ?? [])
    }, [hall.tags])

    const { value: purchasedPremiumPlan } = useIsPremiumPlanEnabled(hall.roomId)

    const courseVitrineEnabled = useMemo(() => {
      return tags.includes(COURSE_VITRINE_TAG)
    }, [tags])

    const watermarkDisabled = useMemo(() => {
      return tags.includes(DISABLE_WATERMARK_TAG)
    }, [tags])

    const issueEnabled = useMemo(() => {
      return tags.includes(ISSUE_TAG)
    }, [tags])

    const vodActivated = useMemo(() => {
      return tags.includes(VOD_ACTIVATED_TAG)
    }, [tags])

    const hideMemberCount = useMemo(() => {
      return tags.includes(HIDE_MEMBER_COUNT_TAG)
    }, [tags])

    const superRoomEnabled = useMemo(() => {
      return tags.includes(SUPER_ROOM_TAG)
    }, [tags])

    const [protectContent, setProtectContent] = useState(hall.protectContent)

    const toggleProtectContent = useCallback(() => {
      if (!protectContent && !purchasedPremiumPlan) {
        analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
          roomId: hall.roomId,
          source: 'HALL_SETTINGS',
          option: 'PROTECT_CONTENT',
        })
        showBuyPremiumFeatureDialog({
          featureType: PremiumFeatureType.PROTECT_CONTENT,
          hallId: hall.roomId,
        })
        return
      }
      setProtectContent(!protectContent)
    }, [hall, purchasedPremiumPlan, protectContent])

    const toggleTag = useCallback(
      (tag: string) => {
        const hasTag = tags.includes(tag)
        if (
          !hasTag &&
          !purchasedPremiumPlan &&
          (tag === COURSE_VITRINE_TAG ||
            tag === ISSUE_TAG ||
            tag === VOD_ACTIVATED_TAG ||
            tag === HIDE_MEMBER_COUNT_TAG)
        ) {
          analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
            roomId: hall.roomId,
            source: 'HALL_SETTINGS',
            option: tag,
          })
          showBuyPremiumFeatureDialog({
            featureType:
              tag === COURSE_VITRINE_TAG
                ? PremiumFeatureType.COURSE_VITRINE
                : tag === VOD_ACTIVATED_TAG
                  ? PremiumFeatureType.VOD_ACTIVATED_TAG
                  : tag === HIDE_MEMBER_COUNT_TAG
                    ? PremiumFeatureType.HIDE_MEMBER_COUNT
                    : PremiumFeatureType.QUESTION_AND_ANSWER,
            hallId: hall.roomId,
          })
          return
        }

        setTags(prevTags =>
          prevTags.includes(tag)
            ? removeTagFromHallTags(tag, prevTags || [])
            : addTagToHallTags(tag, tags || []),
        )
      },
      [hall, tags, purchasedPremiumPlan],
    )

    const onOpenUserSessionsLimit = useCallback(() => {
      if (purchasedPremiumPlan) openUserSessionsLimit(hall.roomId)
      else {
        analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
          roomId: hall.roomId,
          source: 'HALL_SETTINGS',
          option: 'USER_SESSIONS_LIMIT',
        })
        showBuyPremiumFeatureDialog({
          featureType: PremiumFeatureType.USER_SESSIONS_LIMIT,
          hallId: hall.roomId,
        })
      }
    }, [purchasedPremiumPlan, hall.roomId])

    useImperativeHandle(
      ref,
      () => ({
        getCourseVitrine: () => courseVitrineEnabled,
        getIssue: () => issueEnabled,
        getProtectContent: () => protectContent,
        getTags: () => tags,
        getVodActivated: () => vodActivated,
        getHideMemberCount: () => hideMemberCount,
        getWatermarkDisabled: () => watermarkDisabled,
        getSuperRoom: () => superRoomEnabled,
      }),
      [
        courseVitrineEnabled,
        issueEnabled,
        protectContent,
        tags,
        vodActivated,
        hideMemberCount,
        watermarkDisabled,
        superRoomEnabled,
      ],
    )
    const { value: userSessionsLimit } = useUserSessionsLimit(hall.roomId)

    return (
      <HallAdvancedSettingsOptions
        courseVitrineEnabled={courseVitrineEnabled}
        issueEnabled={issueEnabled}
        protectContent={protectContent}
        toggleTag={toggleTag}
        watermarkDisabled={watermarkDisabled}
        toggleProtectContent={toggleProtectContent}
        vodActivated={vodActivated}
        userSessionsLimit={userSessionsLimit}
        hideMemberCount={hideMemberCount}
        superRoomEnabled={superRoomEnabled}
        onOpenUserSessionsLimit={onOpenUserSessionsLimit}
      />
    )
  },
)
